import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Box, Container, Typography, InputBase, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import CloseIcon from '@mui/icons-material/CloseOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBackIosNew';

import { colorIdentidad, getRayo, ORDEN_ENTIDADES } from '../config/rayos';
import { formatoTitulo } from '../config/titulos';
import { useModo } from '../config/modo';
import { useTranslation } from 'react-i18next';
import { IMAGENES_VOZ } from '../config/imagenes';
import Emblema from '../components/Emblema';
import { paginasQueResponden } from '../config/paginas';
import { TEMAS as DEFINICIONES_SABER, estadosConPiezas, temasConPiezas as saberConPiezas, slugsDePuerta } from '../config/heridas';
import { nombreVoz, ID_VOZ } from '../config/nombres_voz';
import { temasConPiezas, slugsDelTema } from '../config/temas';
import { teachingOptions } from '../config/ensenanzas33';
import { useCatalog, useDoors, useSearch, useThirdParty } from '../content/hooks';
import { useLector } from '../config/lector';
import Filtros from '../components/Filtros';
import { EN_EL_TABLERO, ALCANCE } from '../config/alcance';
import './biblioteca.css';

/**
 * LA BIBLIOTECA.
 *
 * Decision de Faina (26-ago): «tu debes darme la mejor propuesta de busqueda, y
 * ahi es donde creo que debemos agregar las imagenes de las entidades. Debe ser
 * bonita, facil de acceder».
 *
 * Por eso la pagina tiene DOS PUERTAS y ninguna tapa a la otra:
 *
 *   1. LA CAJA, arriba del todo. Una sola, sin filtros que estorben. Busca
 *      DENTRO DEL TEXTO, no solo en el titulo — quien escribe «perdon» quiere
 *      las canalizaciones que hablan de perdon, esten como esten tituladas — y
 *      ensena la frase donde aparece la palabra, para que se vea por que salio.
 *      Ignora acentos y mayusculas, y acepta las palabras en cualquier orden.
 *
 *   2. LAS VOCES, debajo, en emblemas grandes con su rayo. Es la entrada
 *      bonita: quien no sabe que busca, entra por quien le habla. Aqui es donde
 *      viven las imagenes de las entidades — hoy el emblema dibujado, manana el
 *      retrato o el simbolo hecho con IA, sin tocar esta pagina.
 *
 * TRABAJADORES DE LA LUZ NO TIENE BLOQUE AQUI (Faina, 26-ago). Se llega por su
 * entrada del menu y ESCRIBIENDO SU NOMBRE EN ESTE BUSCADOR, que los encuentra
 * los tres se escriba en singular o en plural — el destinatario entra en el
 * indice desde el ID, no desde las palabras del texto (destinatarioBuscable).
 *
 * LAS ENSENANZAS NO SON FILTRO (Faina, 26-ago): «no son filtros, deben ser
 * parte del contenido que se agrega en el plan 3 y requieren mas desarrollo».
 *
 * El orden por defecto es alfabetico dentro de cada voz: es el unico que el
 * lector puede predecir, y con 278 piezas eso importa mas que la novedad.
 */

const sinAcentos = (s = '') =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/**
 * Las voces, TODAS. Una sin piezas publicadas no se esconde: se ensena
 * diciendo cuantas vienen, porque ocultarla seria hacer creer que no existe
 * (Faina, 26-ago: «incluye Los Esenios en todas las entidades del sitio»).
 */
/* 🔴 SE CALCULA CON LO QUE EL LECTOR PUEDE VER, no con las 102 de siempre.
   Era una constante de módulo, y con el corpus dentro la página llegó a decir
   «5.880 canalizaciones» mientras los emblemas de las voces sumaban 102. Dos
   cifras que se contradicen en la misma pantalla es exactamente lo que la regla
   de Faina prohíbe. */
/* Separador de miles: con el corpus dentro se pasa de «102» a «5.880», y sin
   el punto los numeros grandes se leen mal. */
const miles = (n) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, '.');

const porVozDe = (base) => ORDEN_ENTIDADES
  .map((e) => ({
    entidad: e,
    piezas: base.filter((c) => c.entidad === e).sort((a, b) => a.titulo.localeCompare(b.titulo, 'es')),
    enCamino: EN_EL_TABLERO[e] || 0
  }))
  .filter((v) => v.piezas.length > 0 || v.enCamino > 0);

/** La frase donde aparece la palabra, con la palabra marcada. */
const fragmento = (pieza, palabras) => {
  const t = pieza.texto;
  const plano = sinAcentos(t);
  let pos = -1;
  palabras.forEach((p) => {
    const i = plano.indexOf(p);
    if (i >= 0 && (pos < 0 || i < pos)) pos = i;
  });
  if (pos < 0) return [{ t: pieza.entrada, marca: false }];
  const punto = t.lastIndexOf('. ', pos);
  const desde = punto >= 0 && pos - punto < 160 ? punto + 2 : Math.max(0, pos - 80);
  const trozo = t.slice(desde, desde + 230).trim();
  const planoTrozo = sinAcentos(trozo);
  const salida = [];
  let cursor = 0;
  while (cursor < trozo.length) {
    let mejor = -1;
    let largo = 0;
    for (let k = 0; k < palabras.length; k += 1) {
      const i = planoTrozo.indexOf(palabras[k], cursor);
      if (i >= 0 && (mejor < 0 || i < mejor)) {
        mejor = i;
        largo = palabras[k].length;
      }
    }
    if (mejor < 0) {
      salida.push({ t: trozo.slice(cursor), marca: false });
      break;
    }
    if (mejor > cursor) salida.push({ t: trozo.slice(cursor, mejor), marca: false });
    salida.push({ t: trozo.slice(mejor, mejor + largo), marca: true });
    cursor = mejor + largo;
  }
  return salida;
};

/* ── B · LAS PAGINAS (Faina, 1-sep) ───────────────────────────────────────
   «Ver más» de 200 en 200 son 29 pulsaciones para llegar al final de 5.880, y
   sin saber nunca por dónde vas. El mismo control que la sala del corpus: se
   escribe el número y se salta. Con catálogos cortos no aparece. */
const Paginas = ({ pagina, total, alIr }) => {
  const { t } = useTranslation();
  if (total <= 1) return null;
  return (
    <Box className="bib-paginas">
      <button type="button" className="bib-pag" disabled={pagina === 1}
              onClick={() => alIr(pagina - 1)}>{t('biblio.anterior')}</button>
      <span className="bib-pag-de">
        {t('biblio.pagina')}{' '}
        <input className="bib-pag-n" type="number" min={1} max={total} value={pagina}
               onChange={(e) => alIr(Number(e.target.value))} />
        {' '}{t('biblio.de')} {miles(total)}
      </span>
      <button type="button" className="bib-pag" disabled={pagina === total}
              onClick={() => alIr(pagina + 1)}>{t('biblio.siguiente')}</button>
    </Box>
  );
};

const Ficha = ({ pieza, modo, palabras, onIr }) => {
  const { t } = useTranslation();
  const color = colorIdentidad(pieza.entidad, modo);
  const trozos = palabras.length ? fragmento(pieza, palabras) : [{ t: pieza.entrada, marca: false }];
  return (
    <button type="button" className="bib-ficha" onClick={() => onIr(pieza.slug)} style={{ '--rayo': color }}>
      <span className="bib-ficha-barra" />
      <span className="bib-ficha-cuerpo">
        <span title={pieza.titulo} className="bib-ficha-titulo">{formatoTitulo(pieza.titulo)}</span>
        <span className="bib-ficha-voz">
          {nombreVoz(pieza.entidad, t)}
          {/* 🔴 QUIEN LA CANALIZO, EN TODAS LAS FICHAS (Faina, 1-sep: «¿por qué
              en las de ONG no salgo como canalizador?»).

              Primero lo puse solo en las del corpus, para poder distinguirlas.
              Estaba mal por dos razones: la de menos es que se distinguen igual
              por el nombre; la de fondo es que **Faina es el canalizador de las
              nuestras** y ocultarlo justo en las suyas era negarle el crédito
              que sí se le da a los 248 del corpus. La ficha dice siempre quién
              recibió el mensaje. */}
          <span className="bib-ficha-quien"> · {pieza.canalizador}</span>
        </span>
        {/* Todas enseñan su entrada: las nuestras salen del texto completo que
            viaja en el bundle, y las del corpus de los 200 primeros caracteres
            que ahora lleva su indice. Antes aqui solo cabia la duracion, y una
            ficha sin texto se leia como una ficha rota. */}
        <span className="bib-ficha-extracto">
          {trozos.map((x, i) => (x.marca
            ? <mark key={i}>{x.t}</mark>
            : <React.Fragment key={i}>{x.t}</React.Fragment>))}
          {trozos.some((x) => x.t) ? '…' : ''}
        </span>
      </span>
    </button>
  );
};

/**
 * LAS PUERTAS QUE SE PULSAN, debajo de la caja.
 *
 * 🔴 POR QUE EXISTEN (Faina, 28-ago). Quien llega aqui llega dolido, y nombrar
 * lo que a uno le pasa es dificil — mucho mas dificil que reconocerlo en una
 * lista. Una caja vacia EXIGE saber la palabra; estas la ofrecen. De paso
 * enseñan que clase de cosas se buscan en este sitio, que es lo que nadie sabe
 * al llegar.
 *
 * ⚠️⚠️ Y NO SE INVENTARON AQUI: YA EXISTIAN Y NO FUNCIONABAN. La portada tiene
 * desde el 26-ago sus dos columnas de puertas —«Lo que te duele» y «Lo que
 * quieres saber»— que enlazan a `/canalizaciones?estado=<id>`. Pero la
 * Biblioteca NUNCA leyo ese parametro: solo habia un comentario anunciandolo.
 * Las 21 puertas de la portada llevaban al catalogo entero, sin filtrar.
 *
 * Asi que en vez de inventar ocho palabras nuevas, se usan LAS MISMAS, y de
 * paso se arregla el enlace roto. Ademas estan mejor escritas que las mias:
 * dicen «Tengo miedo» y «No me perdono», en la lengua del que llega, no
 * «miedo» y «culpa», que es la lengua del que archiva.
 */
/* ⚠️ LAS DOS FAMILIAS NO SE MEZCLAN (28-ago). Juntas eran 21 pildoras y tres
   filas: demasiado para un primer vistazo, y la segunda familia —«lo que
   quieres saber»— empujaba los resultados fuera de la pantalla.
   Se enseñan las DIEZ del dolor, que son la puerta principal del sitio, y las
   once del saber se piden. */

const THIRD_PARTY_PAGE_SIZE = 24;

const groupByLot = (items) => items.reduce((groups, item) => {
  const last = groups[groups.length - 1];
  if (last && last.lote === item.lote) last.piezas.push(item);
  else groups.push({ lote: item.lote, piezas: [item] });
  return groups;
}, []);

const TercerosSeccion = ({ modo, onIr }) => {
  const { t } = useTranslation();
  const [pagina, setPagina] = useState(1);
  const { items, total, loading, error } = useThirdParty(pagina, THIRD_PARTY_PAGE_SIZE, true);
  const grupos = useMemo(() => groupByLot(items), [items]);
  const paginas = Math.max(1, Math.ceil(total / THIRD_PARTY_PAGE_SIZE));
  if (!loading && !error && total === 0) return null;
  return (
    <Box className="bib-seccion">
      <Typography className="bib-rotulo bib-rotulo-2">{t('biblio.de_terceros')}</Typography>
      {error && <Typography className="bib-cuenta">{t('biblio.error_carga')}</Typography>}
      {grupos.map((grupo) => (
        <React.Fragment key={grupo.lote}>
          <Typography className="bib-grupo">{t('biblio.lote', { lote: grupo.lote })}</Typography>
          <Box className="bib-lista">
            {grupo.piezas.map((p) => (
              <Ficha key={p.id} pieza={{ ...p, entrada: p.extracto }} modo={modo} palabras={[]} onIr={onIr} />
            ))}
          </Box>
        </React.Fragment>
      ))}
      <Paginas pagina={pagina} total={paginas}
               alIr={(n) => setPagina(Math.min(paginas, Math.max(1, n || 1)))} />
    </Box>
  );
};

const BibliotecaPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { modo } = useModo();
  const { items: catalogo, loading: cargandoCatalogo, error: errorCatalogo } = useCatalog();
  const { doors } = useDoors();
  const { plan: planLector } = useLector();
  const PUERTAS_DOLOR = useMemo(() => estadosConPiezas(doors), [doors]);
  const PUERTAS_SABER = useMemo(() => saberConPiezas(doors), [doors]);
  const INDICE = useMemo(() => catalogo.map((c) => ({
    ...c,
    texto: '',
    entrada: c.extracto
  })), [catalogo]);
  const [params, setParams] = useSearchParams();
  /* 🔴 LA BUSQUEDA TAMBIEN VIVE EN LA URL (Faina, 1-sep: «me meto a una
     canalización y cuando vuelvo atrás no me deja en el paso anterior»).

     Los filtros ya iban en la dirección; la búsqueda no, era solo estado del
     componente. Así que al volver de una pieza la dirección traía los filtros
     bien y la caja aparecía vacía: se había perdido lo único que el lector
     había escrito con sus manos.

     Se escribe con `replace` para no llenar el historial de una entrada por
     letra tecleada — si no, «atrás» retrocedería letra a letra. */
  /* ⬅️ VOLVER DONDE ESTABA (Faina, 1-sep).

     🔴 LA ALTURA NO VIAJA EN EL ROUTER, VIVE EN `sessionStorage`. El primer
     intento la mandaba en el `state` de la navegacion, y se perdia por el
     camino: el enlace «La biblioteca» es un `Button` de MUI con `component=
     {RouterLink}`, y ahi el `state` no llega —comprobado en pantalla, llegaba
     `null`—. Guardarla aparte, atada a la direccion exacta, funciona con
     cualquier forma de volver: el enlace, el boton «atras» del navegador o una
     recarga.

     Se guarda por DIRECCION COMPLETA: volver a «?q=perdon» repone la altura de
     esa busqueda, no la de otra. */
  const location = useLocation();
  const CLAVE_Y = 'jeshua_biblioteca_y';

  useEffect(() => {
    const apuntar = () => {
      try {
        window.sessionStorage.setItem(CLAVE_Y, JSON.stringify({
          donde: window.location.pathname + window.location.search,
          y: Math.round(window.scrollY),
        }));
      } catch (e) { /* sin sessionStorage se vive igual, solo sin memoria */ }
    };
    window.addEventListener('scroll', apuntar, { passive: true });
    /* ⚠️ NO se apunta al desmontar: para entonces la direccion YA es la de la
       canalizacion, y la nota quedaba guardada como «/canalizacion/… → 0», que
       es justo lo contrario de lo que hace falta. Se apunta solo mientras se
       navega la lista, que es cuando la direccion es la correcta. */
    return () => window.removeEventListener('scroll', apuntar);
  }, []);

  useEffect(() => {
    let guardado = null;
    try { guardado = JSON.parse(window.sessionStorage.getItem(CLAVE_Y) || 'null'); } catch (e) { guardado = null; }
    if (!guardado || guardado.donde !== location.pathname + location.search || !guardado.y) return undefined;
    /* Se espera a que la lista este pintada: si se salta antes, la pagina
       todavia no mide lo suficiente y el navegador se queda a medio camino. */
    const t = setTimeout(() => window.scrollTo(0, guardado.y), 120);
    return () => clearTimeout(t);
  }, [location.pathname, location.search]);

  const [q, setQ] = useState(params.get('q') || '');

  useEffect(() => {
    const enLaUrl = params.get('q') || '';
    if (enLaUrl === q) return;
    const t = setTimeout(() => {
      const pa = new URLSearchParams(window.location.search);
      if (q) pa.set('q', q); else pa.delete('q');
      setParams(pa, { replace: true });
    }, 250);
    return () => clearTimeout(t);
  }, [q]);            // eslint-disable-line react-hooks/exhaustive-deps

  /* Y si la dirección cambia por detrás —el botón «atrás» del navegador—, la
     caja se pone al día. Sin esto, volver dejaba la URL con la búsqueda y la
     caja vacía, que es peor que las dos cosas por separado. */
  useEffect(() => {
    const enLaUrl = params.get('q') || '';
    setQ((antes) => (antes === enLaUrl ? antes : enLaUrl));
  }, [params]);
  const voz = params.get('voz') || '';
  const caja = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => {
      const p = new URLSearchParams(window.location.search);
      if (q) p.set('q', q); else p.delete('q');
      setParams(p, { replace: true });
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const palabras = useMemo(
    () => sinAcentos(q).split(/\s+/).filter((p) => p.length >= 2),
    [q]
  );

  /**
   * LOS RESULTADOS, EN GRUPOS — no en una lista sola.
   *
   * 🔴 EL PROBLEMA QUE RESUELVE (Faina, 28-ago). Buscar en el texto completo
   * devuelve demasiado: medido sobre las 94, «amor» salia en 85 —el 90 % del
   * catalogo—, «miedo» en 47, «luz» en 47, «dolor» en 40. Quien escribia
   * «miedo» recibia 47 fichas identicas; pero no son 47 canalizaciones sobre el
   * miedo: son DOS sobre el miedo y 45 donde la palabra aparece de paso.
   *
   * Y para alguien que llega dolido y escribe la palabra de su herida, eso es
   * PEOR que no encontrar nada: le devuelves el catalogo entero y le haces
   * elegir. Antes el orden ya ponia las del titulo primero, pero en una lista
   * seguida eso no se ve — hay que ir leyendo para descubrirlo.
   *
   * Ahora llegan separadas y con su rotulo: las que TRATAN el tema, las que solo
   * lo NOMBRAN, y las paginas del sitio que hablan de eso. Nada se esconde;
   * queda dicho lo que es cada cosa.
   */
  const TODO = INDICE;

  const POR_VOZ = useMemo(() => porVozDe(TODO), [TODO]);

  /* 🔴 SE BUSCA POR CUANTAS VECES LO DICE, NO POR SI LO DICE (Faina, 30-sep:
     «si dice miedo una vez no lo incluye, pero si lo nombra mas de 3 veces
     si»). Antes «sobre» eran las que llevaban la palabra en el TITULO, y con
     los titulos nuevos eso dejo de decir de que trata una pieza. Ahora «sobre»
     son las que TRATAN del tema —titulo x3 + cuerpo, por encima del umbral— y
     van ordenadas por densidad; las que solo lo mencionan siguen apareciendo
     debajo, sin competir. */
  const [consulta, setConsulta] = useState('');
  useEffect(() => {
    const espera = setTimeout(() => setConsulta(palabras.join(' ')), 300);
    return () => clearTimeout(espera);
  }, [palabras]);
  const busqueda = useSearch(consulta);

  const resultados = useMemo(() => {
    if (!palabras.length) return null;
    const delServidor = busqueda.items.map((c) => ({ ...c, texto: '', entrada: c.extracto }));
    const nombran = delServidor.filter((c) => !c.onTopic);
    const sobre = delServidor.filter((c) => c.onTopic);
    return {
      sobre,
      nombran,
      paginas: paginasQueResponden(palabras),
      total: busqueda.loading ? -1 : sobre.length + nombran.length
    };
  }, [palabras, busqueda]);

  /**
   * Entrar por lo que duele: ?estado=miedo trae las piezas de ese estado.
   * Va antes que el filtro por voz porque es la entrada principal del sitio.
   */

  const elegida = voz ? POR_VOZ.find((v) => v.entidad === voz) : null;
  /* Se lleva la dirección exacta de la lista —búsqueda, filtros y todo— para
     que la pieza sepa a dónde devolver al lector.

     ⚠️ Y la ruta se lee de `location`, no se escribe a mano: escrita, quien
     abría una pieza desde la INTRANET volvía a la biblioteca pública, a una
     lista donde su canalización no está. */
  const irA = (slug) => navigate(`/canalizacion/${slug}`, {
    state: { volverA: `${location.pathname}${window.location.search}` }
  });
  const elegirVoz = (e) => {
    const p = new URLSearchParams(window.location.search);
    if (e) p.set('voz', e); else p.delete('voz');
    setParams(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /**
   * EL ORDEN POR DEFECTO: ALTERNANDO VOCES.
   *
   * Era alfabetico por titulo —«Abrir el corazon», «Algo se quedo trabado»,
   * «Amar la diversidad», «Amarte a ti mismo»— y ese es el orden menos util que
   * existe: nadie busca una canalizacion por su letra inicial, y hacia que la
   * primera pantalla, la que decide si alguien se queda, dependiera del azar del
   * abecedario.
   *
   * ⚠️ NO SE ORDENA POR «MAS RECIENTE» porque el dato NO EXISTE: las piezas no
   * llevan fecha en canalizaciones.json. Prometer un orden por novedad sin tener
   * la fecha seria inventarlo.
   *
   * Lo que si se puede sostener: una de cada voz, y vuelta a empezar. La primera
   * pantalla enseña ocho voces distintas en vez de cuatro piezas que empiezan
   * por A, y el catalogo se ve variado desde arriba. Es estable —no cambia entre
   * visitas— y dentro de cada voz sigue el orden alfabetico, que ahi si sirve
   * para reencontrar algo.
   */
  const todas = useMemo(() => {
    const porVoz = ORDEN_ENTIDADES.map((e) =>
      TODO.filter((c) => c.entidad === e).sort((a, b) => a.titulo.localeCompare(b.titulo, 'es')));
    const mezcla = [];
    for (let i = 0; mezcla.length < TODO.length; i += 1) {
      porVoz.forEach((lista) => { if (lista[i]) mezcla.push(lista[i]); });
    }
    return mezcla;
  }, [TODO]);

  /**
   * LOS CUATRO FILTROS, COMBINABLES (Faina, 26-ago: «los filtros son claves»).
   *
   * Lo que antes no se podia: o elegias voz o elegias tema. Ahora los cuatro se
   * suman —«Miguel» + «Tengo miedo» + «corta»— que es como busca de verdad
   * quien sabe lo que necesita.
   *
   * Cada opcion lleva su cuenta y las vacias NO se ofrecen: no hay manera de
   * llegar a una pantalla sin resultados.
   */
  /* 🔴 EL FILTRO QUE FALTABA. `?estado=` lo escriben la portada y las pildoras
     de aqui arriba, y hasta hoy NADIE lo leia: los 21 enlaces de la portada
     caian en el catalogo sin filtrar. Va el primero de los cinco porque es la
     entrada principal del sitio — entrar por lo que te duele. */
  const fEstado = params.get('estado') || '';
  const fVoz = params.get('voz') || '';
  const fCanal = params.get('canalizador') || '';
  const fTema = params.get('tema') || '';
  const fDura = params.get('dura') || '';
  /* La enseñanza (Def 62): llega desde la pagina «33», donde cada una de las
     33 abre la Biblioteca con su codigo. Cada pieza lleva 1-3 codigos. */
  const fEns = params.get('ensenanza') || '';

  /* ⭐ LOS FILTROS SE HABLAN ENTRE SI (Faina, 1-sep: «cuando filtro por Jeshua,
     después me aparecen canalizadores que no lo canalizan»).

     `cumpleSalvo(clave)` aplica todos los criterios MENOS uno. Con eso, las
     opciones de cada filtro se calculan sobre lo que los demás ya dejaron: si
     la voz es Jeshua, el desplegable de canalizador enseña solo a los que
     canalizan a Jeshua, con su recuento real.

     ⚠️ Y SE EXCLUYE EL PROPIO CAMPO A PROPOSITO. Si las opciones de canalizador
     se calcularan tambien con el canalizador ya elegido, quedaria una sola
     opcion —la elegida— y no se podria cambiar a otra sin limpiar el filtro
     antes. Cada lista enseña «a donde puedo ir desde aqui», no «donde estoy». */
  const cumpleSalvo = useMemo(() => (clave) => (p) => {
    if (clave !== 'estado' && fEstado && !slugsDePuerta(doors, fEstado).includes(p.slug)) return false;
    if (clave !== 'voz' && fVoz && p.entidad !== fVoz) return false;
    if (clave !== 'canalizador' && fCanal && p.canalizador !== fCanal) return false;
    if (clave !== 'tema' && fTema && !slugsDelTema(doors, fTema).includes(p.slug)) return false;
    if (clave !== 'dura' && fDura === 'corta' && p.minutos > 3) return false;
    if (clave !== 'dura' && fDura === 'larga' && p.minutos <= 3) return false;
    if (clave !== 'ensenanza' && fEns && !(p.ensenanzas || []).includes(fEns)) return false;
    return true;
  }, [fEstado, fVoz, fCanal, fTema, fDura, fEns, doors]);

  const cumple = useMemo(() => cumpleSalvo(null), [cumpleSalvo]);

  const filtradas = useMemo(() => TODO.filter(cumple), [cumple, TODO]);
  const hayFiltro = Boolean(fEstado || fVoz || fCanal || fTema || fDura || fEns);

  /* 🔴 LOS FILTROS SE CALCULAN CON EL CATALOGO QUE SE ESTA VIENDO (Faina,
     1-sep: «los filtros no están funcionando en la biblioteca»).

     Estaban atados a `INDICE` —las 102 propias— aunque la lista ya enseñara
     5.880: el desplegable de canalizador ofrecia UNA opcion, «Faina», y los
     recuentos de voz y duracion contaban 102 de 5.880. El filtro existia y no
     filtraba nada de lo que habia debajo.

     Justo lo que este mismo archivo tenia anotado: «el dia que entre el corpus
     con sus 248 canalizadores, este filtro pasa a ser de los mas utiles del
     sitio». Ese dia es hoy, y por eso ahora bebe de `TODO`. */
  const CAMPOS = useMemo(() => {
    /* Cada campo mira el catálogo filtrado por los OTROS. */
    const paraVoz = TODO.filter(cumpleSalvo('voz'));
    const paraCanal = TODO.filter(cumpleSalvo('canalizador'));
    const paraDura = TODO.filter(cumpleSalvo('dura'));
    const paraEns = TODO.filter(cumpleSalvo('ensenanza'));

    const canalizadores = [...new Set(paraCanal.map((c) => c.canalizador))]
      .sort((a, b) => a.localeCompare(b, 'es'))
      .map((c) => ({ id: c, texto: c, n: paraCanal.filter((x) => x.canalizador === c).length }))
      /* Un canalizador sin piezas en lo que se está viendo no es una opción:
         es un callejón sin salida que devolvería una lista vacía. */
      .filter((o) => o.n > 0);
    return [
      {
        clave: 'voz',
        nombre: t('biblio.f_voz'),
        opciones: ORDEN_ENTIDADES
          .map((e) => ({ id: e, texto: e, clave: ID_VOZ[e] ? 'section.' + ID_VOZ[e] : undefined, n: paraVoz.filter((c) => c.entidad === e).length }))
          .filter((o) => o.n > 0)
      },
      {
        /* Hoy tiene UNA opción: todas las nuestras las canaliza Faina. Faina
           lo pidió el 26-ago sabiéndolo. Con los lotes de terceros entran
           también sus canalizadores. */
        clave: 'canalizador',
        nombre: t('biblio.f_canalizador'),
        opciones: canalizadores
      },
      {
        /* Lista curada, no sacada de los titulos en automatico: ver el porque
           en config/temas.js. */
        clave: 'tema',
        nombre: t('biblio.f_tema'),
        opciones: temasConPiezas(doors)
      },
      {
        /* Las 33 enseñanzas (Def 62), con cuantas piezas tiene cada una en lo
           que se esta viendo. Es el mismo filtro que abre la pagina «33». */
        clave: 'ensenanza',
        nombre: t('biblio.f_ensenanza'),
        opciones: teachingOptions(TODO)
          .map((o) => ({ ...o, n: paraEns.filter((c) => (c.ensenanzas || []).includes(o.id)).length }))
          .filter((o) => o.n > 0)
      },
      {
        /* En dos tramos y no en minutos exactos: quien filtra por tiempo quiere
           «algo corto», no «exactamente cuatro minutos». */
        clave: 'dura',
        nombre: t('biblio.f_duracion'),
        opciones: [
          { id: 'corta', clave: 'biblio.dura_corta', texto: 'Corta · hasta 3 min', n: paraDura.filter((c) => c.minutos <= 3).length },
          { id: 'larga', clave: 'biblio.dura_larga', texto: 'Más larga · 4 min o más', n: paraDura.filter((c) => c.minutos > 3).length }
        ]
      }
    ];
  /* `t` va en las dependencias: sin el, los rotulos de los desplegables se
     calculaban con el idioma de la primera carga y salian en ingles en el
     sitio en español (visto 14-sep). */
  }, [TODO, cumpleSalvo, t, doors]);

  const cambiarFiltro = (clave, valor) => {
    const pa = new URLSearchParams(window.location.search);
    if (valor) pa.set(clave, valor); else pa.delete(clave);
    setParams(pa);
    setCuantas(PASO);
  };

  const limpiarFiltros = () => {
    const pa = new URLSearchParams(window.location.search);
    ['estado', 'voz', 'canalizador', 'tema', 'dura', 'ensenanza'].forEach((k) => pa.delete(k));
    setParams(pa);
    setCuantas(PASO);
  };

  /* Cuantas se enseñan de golpe. Con las 94 en una sola columna la pagina media
     16.019 px —unas veinte pantallas— y con las 278 escritas pasaria de 47.000.
     De 24 en 24: cabe el primer vistazo y el resto se pide.
     De 24 en 24 también con los lotes de terceros. */
  const PASO = 24;
  const [cuantas, setCuantas] = useState(24);
  useEffect(() => { setCuantas(PASO); }, [PASO]);

  /* La página de la lista larga. Vuelve a 1 en cuanto cambia lo que se está
     mirando: seguir en la página 14 de otra búsqueda no tiene sentido. */
  const [pagina, setPagina] = useState(1);
  useEffect(() => { setPagina(1); }, [q, fEstado, fVoz, fCanal, fTema, fDura, fEns]);
  const paginasDe = (n) => Math.max(1, Math.ceil(n / PASO));
  const irAPagina = (n, total) => {
    setPagina(Math.min(paginasDe(total), Math.max(1, n || 1)));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /* Cuantas del grupo «la nombran» se enseñan. Cuatro: las justas para que se
     vea que existen sin tapar a las que si tratan el tema. */
  const [nombradas, setNombradas] = useState(4);

  /* Las once del saber, plegadas. Se abren solas si la puerta que viene puesta
     en la direccion es de esa familia — si no, quedaria marcada una pildora que
     no se ve. */
  const [masPuertas, setMasPuertas] = useState(
    () => DEFINICIONES_SABER.some((e) => e.id === (params.get('estado') || ''))
  );

  return (
    <Box className={`bib bib-${modo}`}>
      <Container maxWidth="lg">

        <Box className="bib-cabecera">
          <Typography variant="h1" className="bib-h1">
            {t('biblio.titulo')}
          </Typography>
          {/* SIN BAJADA (Faina, 26-ago: «borra esto en todo el sitio»). Contaba las
              piezas y explicaba cómo funciona el buscador —dos cosas que el lector
              no ha preguntado—. La caja de buscar se explica sola. */}
        </Box>

        <Box className="bib-caja" onClick={() => caja.current && caja.current.focus()}>
          <SearchIcon className="bib-lupa" />
          <InputBase
            inputRef={caja}
            className="bib-input"
            value={q}
            onChange={(e) => { setQ(e.target.value); setNombradas(4); }}
            placeholder={t('menu.busca_placeholder')}
            inputProps={{ 'aria-label': t('biblio.buscar_aria') }}
          />
          {q && (
            <IconButton size="small" className="bib-limpiar" onClick={() => setQ('')} aria-label={t('filtros.limpiar')}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {errorCatalogo && !cargandoCatalogo && (
          <Typography className="bib-cuenta">{t('biblio.error_carga')}</Typography>
        )}

        {/* Las ocho palabras que se pulsan. Van SIEMPRE, tambien con una
            busqueda puesta: asi se cambia de herida con un clic, sin borrar. */}
        <Box className="bib-duelen">
          {(masPuertas ? [...PUERTAS_DOLOR, ...PUERTAS_SABER] : PUERTAS_DOLOR).map((e) => (
            <button
              type="button"
              key={e.id}
              className={`bib-duele${fEstado === e.id ? ' bib-duele-on' : ''}`}
              onClick={() => {
                setQ('');
                setNombradas(4);
                setCuantas(PASO);
                cambiarFiltro('estado', fEstado === e.id ? '' : e.id);
              }}
            >
              {t(e.clave)}<span className="bib-duele-n">{e.n}</span>
            </button>
          ))}
          {/* 🔴 Y LA VUELTA (Faina, 28-ago: «¿cómo vuelve el buscador a las 10
              principales de la herida?»). No volvia: el desplegar era un viaje
              de ida — se abrian las 21 y ya no habia manera de replegarlas.
              El mismo boton hace las dos cosas y dice cual toca.
              Al plegar se retira el filtro si la puerta puesta era de las que
              se esconden: si no, quedaria filtrando una pildora invisible y el
              catalogo apareceria recortado sin motivo a la vista. */}
          <button type="button" className="bib-duele bib-duele-mas"
                  onClick={() => {
                    if (masPuertas && PUERTAS_SABER.some((e) => e.id === fEstado)) {
                      cambiarFiltro('estado', '');
                    }
                    setMasPuertas((v) => !v);
                  }}>
            {masPuertas ? 'volver a lo que te duele' : 'y lo que quieres saber'}
          </button>
        </Box>

        {/* La barra: siempre visible salvo cuando hay una busqueda escrita,
            que manda sobre todo lo demas. */}
        {!resultados && (
          <Filtros
            campos={CAMPOS}
            activos={{ voz: fVoz, canalizador: fCanal, tema: fTema, dura: fDura, ensenanza: fEns }}
            alElegir={cambiarFiltro}
            alLimpiar={limpiarFiltros}
            cuantas={filtradas.length}
          />
        )}

        {resultados && (
          <Box className="bib-seccion">

            {/* ① LAS QUE TRATAN EL TEMA. Con su extracto: son las que buscaba. */}
            {resultados.sobre.length > 0 && (
              <>
                <Typography className="bib-grupo">
                  {t('biblio.sobre_esto')} <span>· {resultados.sobre.length}</span>
                </Typography>
                <Box className="bib-lista">
                  {resultados.sobre.map((x) => (
                    <Ficha key={x.id} pieza={x} modo={modo} palabras={palabras} onIr={irA} />
                  ))}
                </Box>
              </>
            )}

            {/* ② LAS QUE SOLO LO NOMBRAN. De cuatro en cuatro: son el ruido, no
                el hallazgo, y no deben empujar al primer grupo fuera de la
                pantalla. Quien las quiera, las pide. */}
            {resultados.nombran.length > 0 && (
              <>
                <Typography className="bib-grupo">
                  {t('biblio.la_nombran')} <span>· {resultados.nombran.length}</span>
                </Typography>
                <Box className="bib-lista">
                  {resultados.nombran.slice(0, nombradas).map((x) => (
                    <Ficha key={x.id} pieza={x} modo={modo} palabras={palabras} onIr={irA} />
                  ))}
                </Box>
                {nombradas < resultados.nombran.length && (
                  <button type="button" className="bib-mas"
                          onClick={() => setNombradas((n) => n + 8)}>
                    {resultados.nombran.length - nombradas === 1
                      ? t('biblio.ver_la_que_queda')
                      : t('biblio.ver_restantes', { n: resultados.nombran.length - nombradas })}
                  </button>
                )}
              </>
            )}

            {/* ③ LAS PAGINAS DEL SITIO. Al final y en formato distinto, para que
                nunca se confundan con una canalizacion. */}
            {resultados.paginas.length > 0 && (
              <>
                <Typography className="bib-grupo">
                  {t('biblio.en_la_fundacion')} <span>· {resultados.paginas.length}</span>
                </Typography>
                <Box className="bib-paginas">
                  {resultados.paginas.map((pg) => (
                    <button type="button" key={pg.ruta} className="bib-pagina"
                            onClick={() => navigate(pg.ruta)}>
                      <span className="bib-pagina-t">{t(pg.titulo)}</span>
                      <span className="bib-pagina-f">{t(pg.frase)}</span>
                    </button>
                  ))}
                </Box>
              </>
            )}

            {/* ④ NADIE SE VA CON LAS MANOS VACIAS (Faina, 28-ago).
                Antes decia «Ninguna canalizacion con esas palabras» y ahi se
                acababa: frio y sin salida. Quien busca su dolor y no encuentra
                nada tiene que recibir algo — aqui, las catorce voces, que es la
                otra puerta del sitio. No es comodidad, es cuidado. */}
            {resultados.total === 0 && resultados.paginas.length === 0 && (
              <Box className="bib-vacio">
                <Typography className="bib-vacio-t">
                  {t('biblio.vacio')}
                </Typography>
                <Typography className="bib-vacio-s">
                  {/* La cifra no se escribe a mano: el universo cambió de 278 a
                      397 el 1-sep y esta frase se habría quedado vieja sola. */}
                  {t('biblio.vacio_texto', { universo: ALCANCE.universo, publicadas: INDICE.length })}
                </Typography>
                <button type="button" className="bib-mas" onClick={() => setQ('')}>
                  {t('biblio.ver_voces')}
                </button>
              </Box>
            )}
          </Box>
        )}

        {/* LAS VOCES SE QUEDAN (Faina, 26-ago: «me parecía bien las voces»).
            Se enseñan cuando no hay ningun filtro puesto: son la entrada
            visual del catalogo. Con filtro, estorbarian. */}
        {!resultados && !hayFiltro && (
          <>
            <Typography className="bib-rotulo">{t('footer.voces')}</Typography>
            <Box className="bib-voces">
              {POR_VOZ.map(({ entidad, piezas, enCamino }) => {
                const color = colorIdentidad(entidad, modo);
                const imagen = IMAGENES_VOZ[entidad];
                const espera = piezas.length === 0;
                return (
                  <button
                    type="button"
                    key={entidad}
                    className={`bib-voz${espera ? ' bib-voz-espera' : ''}`}
                    onClick={() => elegirVoz(entidad)}
                    style={{ '--rayo': color }}
                  >
                    <span className="bib-voz-luz">
                      {imagen
                        ? <img src={imagen} alt="" className="bib-voz-img" />
                        : <Emblema entidad={entidad} tamano={78} modo={modo} />}
                    </span>
                    <span className="bib-voz-nombre">{nombreVoz(entidad, t)}</span>
                    <span className="bib-voz-n">
                      {espera ? `${enCamino} · en camino` : miles(piezas.length)}
                    </span>
                  </button>
                );
              })}
            </Box>

            <Typography className="bib-rotulo bib-rotulo-2">{t('biblio.todas')}</Typography>
            <Box className="bib-lista">
              {todas.slice((pagina - 1) * PASO, pagina * PASO).map((p) => (
                <Ficha key={p.id} pieza={p} modo={modo} palabras={[]} onIr={irA} />
              ))}
            </Box>
            <Paginas pagina={pagina} total={paginasDe(todas.length)}
                     alIr={(n) => irAPagina(n, todas.length)} />
            {planLector.corpusTerceros && <TercerosSeccion modo={modo} onIr={irA} />}
          </>
        )}

        {/* Con filtro puesto: solo la lista, sin la cuadricula de voces. */}
        {!resultados && hayFiltro && (
          <Box className="bib-seccion">
            <Box className="bib-lista">
              {filtradas.slice((pagina - 1) * PASO, pagina * PASO).map((p) => (
                <Ficha key={p.id} pieza={p} modo={modo} palabras={[]} onIr={irA} />
              ))}
            </Box>
            <Paginas pagina={pagina} total={paginasDe(filtradas.length)}
                     alIr={(n) => irAPagina(n, filtradas.length)} />
            {filtradas.length === 0 && (
              <Typography className="bib-cuenta">{t('biblio.ninguna')}</Typography>
            )}

          </Box>
        )}

      </Container>
    </Box>
  );
};

export default BibliotecaPage;
